import React, { useCallback, useContext, useEffect, useState } from "react";
import { Fade, useScrollTrigger } from "@mui/material";
import ReactImageGallery from "react-image-gallery";
import { isRedirectedFromMobileApp } from "Utils/RedirectedFromMobileApp";
import { useAppContext } from "Context";
import HomeImageGalleryItem from "./HomeImageGalleryItem";
import { useHeader } from "Providers/Header";
import { usePrevious } from "Hooks";
import * as S from "./HomeImageGallery.styles";
import ScrollIndicator from "./ScrollIndicator";
import HomeImageGalleryApi, {
  ImageGallery,
  ImageGalleryChannel,
  ImageGalleryItem,
} from "Api/HomeImageGalleryApi";
import { StationContext } from "StationsContext/Context";

const HomeImageGalleryContent = () => {
  const { stations } = useContext(StationContext);
  const STATIONS = stations;
  const header = useHeader();
  const { activeChannel, paused } = useAppContext();
  const prevActiveChannel = usePrevious(activeChannel);
  const hasScrolledDown = useScrollTrigger({
    disableHysteresis: true,
    threshold: 25,
  });
  const [imageGallery, setImageGallery] = useState({
    expiration: new Date().getTime(),
  } as ImageGallery);

  const setMode = useCallback(
    (imageIndex: number) => {
      const id = STATIONS[activeChannel].id;
      const imageGalleryChannel = imageGallery[id] as ImageGalleryChannel;
      const items: ImageGalleryItem[] = imageGalleryChannel?.items || [];
      if (items && items.length) {
        header.setMode(items[imageIndex].headerMode || "dark");
      } else {
        header.setMode("dark");
      }
    },
    [activeChannel, header, imageGallery, STATIONS]
  );

  useEffect(() => {
    setMode(0);
    async function refresh() {
      const gallery = await HomeImageGalleryApi.getImageGallery();
      setImageGallery(gallery);
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeChannel !== prevActiveChannel) {
      setMode(0);
    }
    /** {@link prevActiveChannel} is from a ref and can be ignored as a dependency. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannel, setMode]);
  const imageGalleryChannel = imageGallery[
    STATIONS[activeChannel].id
  ] as ImageGalleryChannel;
  return (
    <S.Container className={S.classes.root}>
      <ReactImageGallery
        renderItem={(item) => (
          <HomeImageGalleryItem
            channelTitle={imageGalleryChannel?.channelTitle || ""}
            channelDescription={imageGalleryChannel?.channelDescription || ""}
            {...(item as any)}
          />
        )}
        items={imageGalleryChannel?.items || []}
        showThumbnails={false}
        showFullscreenButton={false}
        showPlayButton={false}
        showNav={false}
        showBullets={false}
        autoPlay={true}
        slideInterval={8000}
        onSlide={(i) => setMode(i)}
      />

      <Fade in={!hasScrolledDown}>
        <div>
          <Fade in={paused}>
            <S.PlayPrompt variant="subtitle1" component="p">
              Press Play to start listening
            </S.PlayPrompt>
          </Fade>

          <ScrollIndicator />
        </div>
      </Fade>
    </S.Container>
  );
};

const HomeImageGallery = () => {
  return isRedirectedFromMobileApp() ? null : <HomeImageGalleryContent />;
};

export default HomeImageGallery;
